import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  Apps,
  Settings,
  ExitToApp,
  QueueMusic,
  Campaign,
  Radio,
  SupervisorAccount,
  History,
  Dashboard,
  Block,
  SystemUpdateAlt,
  Tv,
  DeleteSweep
} from '@mui/icons-material';

const drawerWidth = 240;

const Layout = ({ children, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Генерируем URL для Swagger документации
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  const docsUrl = `${apiUrl.replace('/api/v1', '')}/docs`;
  const clientDocsUrl = `${apiUrl.replace('/api/v1', '')}/client-docs`;

  const handleClearCache = async () => {
    if (!window.confirm('Вы уверены что хотите очистить весь кеш? Это может повлиять на производительность до повторного кеширования.')) {
      return;
    }
    
    setClearingCache(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/admin/cache/clear`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        alert('✅ Кеш успешно очищен!');
      } else {
        const error = await response.json();
        alert(`❌ Ошибка: ${error.detail || 'Не удалось очистить кеш'}`);
      }
    } catch (error) {
      alert(`❌ Ошибка сети: ${error.message}`);
    } finally {
      setClearingCache(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: 'Все каналы', icon: <Tv />, path: '/channels' },
    { text: 'Плейлисты ТВ', icon: <QueueMusic />, path: '/playlists' },
    { text: 'Радио', icon: <Radio />, path: '/radio/playlists' },
    { text: 'Приложения', icon: <Apps />, path: '/applications' },
    { text: 'Телепрограмма', icon: <Tv />, path: '/tv-guide' },
    { text: 'Реклама', icon: <Campaign />, path: '/ads' },
    { text: 'Блокировка рекламы', icon: <Block />, path: '/ad-blocking' },
    { text: 'Мониторинг', icon: <Dashboard />, path: '/monitoring' },
    { text: 'Администраторы', icon: <SupervisorAccount />, path: '/admin-users' },
    { text: 'Журнал действий', icon: <History />, path: '/admin-logs' },
    { text: 'Настройки', icon: <Settings />, path: '/settings' },
  ];

  const isCurrentPath = (path) => {
    if (path === '/playlists') {
      return location.pathname === '/playlists' || location.pathname.startsWith('/playlists/');
    }
    if (path === '/channels') {
      return location.pathname === '/channels' || location.pathname.startsWith('/channels/');
    }
    if (path === '/radio/playlists') {
      return location.pathname.startsWith('/radio/');
    }
    if (path === '/applications') {
      return location.pathname === '/applications' || location.pathname.startsWith('/applications/');
    }
    if (path === '/app-updates') {
      return location.pathname === '/app-updates' || location.pathname.startsWith('/app-updates/');
    }
    if (path === '/ads') {
      return location.pathname === '/ads' || location.pathname.startsWith('/ads/');
    }
    return location.pathname === path;
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Заголовок drawer */}
      <Toolbar sx={{ 
        backgroundColor: isMobile ? 'primary.main' : 'transparent',
        color: isMobile ? 'primary.contrastText' : 'inherit'
      }}>
        <Typography variant="h6" noWrap component="div" sx={{
          fontWeight: 600,
          fontSize: isMobile ? '1.1rem' : '1.25rem'
        }}>
          TV Admin
        </Typography>
      </Toolbar>
      
      {isMobile && <Divider />}
      
      {/* Навигационное меню */}
      <List sx={{ flex: 1, pt: isMobile ? 1 : 0 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: isMobile ? 0.5 : 0 }}>
            <ListItemButton
              selected={isCurrentPath(item.path)}
              onClick={() => {
                navigate(item.path);
                // Закрываем мобильное меню после навигации
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                minHeight: isMobile ? 56 : 48, // Touch-friendly высота для мобильных
                px: isMobile ? 3 : 2,
                pl: item.indent ? (isMobile ? 5 : 4) : (isMobile ? 3 : 2), // Отступ для подразделов
                borderRadius: isMobile ? 2 : 1,
                mx: isMobile ? 1 : 0,
                '&.Mui-selected': {
                  backgroundColor: isMobile ? 'primary.light' : 'action.selected',
                  color: isMobile ? 'primary.contrastText' : 'inherit',
                  '&:hover': {
                    backgroundColor: isMobile ? 'primary.main' : 'action.selected',
                  },
                },
                '&:hover': {
                  backgroundColor: isMobile ? 'primary.light' : 'action.hover',
                  borderRadius: isMobile ? 2 : 1,
                }
              }}
            >
              <ListItemIcon sx={{
                color: isCurrentPath(item.path) && isMobile ? 'primary.contrastText' : 'inherit',
                minWidth: isMobile ? 48 : 40
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{
                  fontSize: item.indent ? (isMobile ? '0.9rem' : '0.8125rem') : (isMobile ? '0.95rem' : '0.875rem'),
                  fontWeight: isCurrentPath(item.path) ? 600 : 400
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      {/* Кнопка выхода в мобильном drawer */}
      {isMobile && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<ExitToApp />}
            onClick={() => {
              onLogout();
              setMobileOpen(false);
            }}
            sx={{
              minHeight: 48,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.95rem'
            }}
          >
            Выйти
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          zIndex: theme.zIndex.drawer + 1
        }}
      >
        <Toolbar sx={{ 
          minHeight: isMobile ? 56 : 64, // Компактная высота для мобильных
          px: isMobile ? 2 : 3
        }}>
          {/* Кнопка меню для мобильных */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: isMobile ? 1 : 2, 
              display: { sm: 'none' },
              minWidth: 44,
              minHeight: 44 // Touch-friendly размер
            }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Заголовок */}
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: isMobile ? '1.1rem' : '1.25rem',
              fontWeight: isMobile ? 500 : 400
            }}
          >
            {isMobile ? 'Админ панель' : 'Административная панель'}
          </Typography>
          
          {/* Кнопка очистки кеша */}
          <Button 
            color="inherit" 
            onClick={handleClearCache}
            disabled={clearingCache}
            startIcon={<DeleteSweep />}
            sx={{
              textTransform: 'none',
              minHeight: 40,
              mr: 1,
              fontSize: isMobile ? '0.7rem' : '0.875rem',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }
            }}
          >
            {clearingCache ? '...' : (isMobile ? '🧹' : '🧹 Сбросить кеш')}
          </Button>

          {/* Кнопки API Docs */}
          <Button 
            color="inherit" 
            href={docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textTransform: 'none',
              minHeight: 40,
              mr: 1,
              fontSize: isMobile ? '0.7rem' : '0.875rem'
            }}
          >
            {isMobile ? '🔐' : '🔐 Admin API'}
          </Button>
          
          <Button 
            color="inherit" 
            href={clientDocsUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textTransform: 'none',
              minHeight: 40,
              mr: 2,
              fontSize: isMobile ? '0.7rem' : '0.875rem'
            }}
          >
            {isMobile ? '📱' : '📱 Client API'}
          </Button>
          
          {/* Кнопка выхода (только на десктопе) */}
          {!isMobile && (
            <Button 
              color="inherit" 
              onClick={onLogout} 
              startIcon={<ExitToApp />}
              sx={{
                textTransform: 'none',
                minHeight: 40
              }}
            >
              Выйти
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: isMobile ? 2 : 3, // Меньшие отступы на мобильных
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100vw', // Предотвращаем горизонтальный скролл
          overflow: 'hidden' // Предотвращаем переполнение
        }}
      >
        <Toolbar sx={{ minHeight: isMobile ? 56 : 64 }} />
        <Box sx={{ 
          width: '100%',
          maxWidth: '100%',
          overflow: 'auto' // Разрешаем скролл для контента
        }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
